import React, { useEffect, useMemo, useRef, useState } from "react";
import { View, Text, TouchableOpacity, ScrollView } from "react-native";
import { Ionicons, Feather } from "@expo/vector-icons";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";

import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import GlobalStyles from "../../../Resources/Styles";
import { useDevice, useScreenDimensions } from "../../_layout";
import StatisticsExerciseSearchFloating from "./StatisticsExerciseSearchFloating";
import ExerciseTrackPill from "./ExerciseTrackPill";
import Tooltip from "../../Components/Tooltip";
import FloatingMenu from "../../Components/FloatingMenu";

const PILL_GAP = 8;
const AUTO_SCROLL_PX_PER_FRAME = 10;
const ARROW_SCROLL_STEP_PX = 280;

const HoverableAddButton = ({ onPress, disabled }) => {
  const { isMobile } = useDevice();
  const [isHovered, setIsHovered] = useState(false);

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        height: 30,
        width: 30,
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 12,
        borderWidth: 1,
        borderColor: Colors.bubbleColor,
        borderStyle: "dashed",
        backgroundColor: isHovered ? Colors.borderColor : "transparent",
        opacity: disabled ? 0.4 : 1,
      }}
    >
      <Ionicons name="add" size={18} color={Colors.bubbleColor} />
    </TouchableOpacity>
  );
};

const SkeletonChip = () => (
  <View
    style={{
      height: 30,
      borderRadius: 12,
      paddingHorizontal: 12,
      minWidth: 120,
      backgroundColor: Colors.lightGrayColor,
      opacity: 0.7,
    }}
  />
);

const Chip = ({ label, selected, disabled, onPress }) => {
  const { isMobile } = useDevice();
  const [isHovered, setIsHovered] = useState(false);

  const bg = selected
    ? isHovered
      ? Colors.messageBubbleColor
      : Colors.bubbleColor
    : isHovered
      ? Colors.borderColor
      : Colors.lightGrayColor;

  return (
    <TouchableOpacity
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <View
        style={{
          paddingHorizontal: 12,
          height: 30,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: bg,
          backgroundColor: bg,
          alignItems: "center",
          justifyContent: "center",
          opacity: disabled ? 0.35 : 1,
        }}
      >
        <Text
          style={{
            fontSize: 12,
            fontWeight: selected ? "600" : "400",
            color: selected ? Colors.whiteColor : Colors.darkgrayColor,
          }}
        >
          {label}
        </Text>
      </View>
    </TouchableOpacity>
  );
};

const ScrollableRow = ({ children, style, contentContainerStyle }) => {
  const { isMobile } = useDevice();
  const scrollRef = useRef(null);
  const [isRowHovered, setIsRowHovered] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const autoScrollFrameRef = useRef(null);
  const autoScrollDirectionRef = useRef(0);
  const lastAutoScrollTsRef = useRef(0);
  const userWheelTsRef = useRef(0);

  const stopAutoScroll = () => {
    if (autoScrollFrameRef.current) {
      cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
    autoScrollDirectionRef.current = 0;
  };

  const updateScrollEdges = () => {
    if (isMobile) return;
    const node = scrollRef.current?.getScrollableNode?.();
    if (!node) return;

    const left = Number(node.scrollLeft ?? 0) || 0;
    const width = Number(node.clientWidth ?? 0) || 0;
    const scrollWidth = Number(node.scrollWidth ?? 0) || 0;
    const epsilon = 1;

    setCanScrollLeft(left > epsilon);
    setCanScrollRight(left + width < scrollWidth - epsilon);
  };

  const startAutoScroll = (direction) => {
    if (isMobile) return;
    stopAutoScroll();
    autoScrollDirectionRef.current = direction;

    const tick = () => {
      const node = scrollRef.current?.getScrollableNode?.();
      if (!node) {
        stopAutoScroll();
        return;
      }

      const directionNow = autoScrollDirectionRef.current;
      if (directionNow === 0) {
        stopAutoScroll();
        return;
      }

      // Respect current edges and stop when we can't scroll further.
      if (directionNow < 0 && !canScrollLeft) {
        stopAutoScroll();
        return;
      }
      if (directionNow > 0 && !canScrollRight) {
        stopAutoScroll();
        return;
      }

      const delta = AUTO_SCROLL_PX_PER_FRAME * directionNow;
      node.scrollLeft = (Number(node.scrollLeft ?? 0) || 0) + delta;
      lastAutoScrollTsRef.current = performance.now();
      updateScrollEdges();
      autoScrollFrameRef.current = requestAnimationFrame(tick);
    };

    autoScrollFrameRef.current = requestAnimationFrame(tick);
  };

  const scrollLeft = () => {
    if (!isMobile) {
      const node = scrollRef.current?.getScrollableNode();
      if (node) {
        node.scrollBy({ left: -ARROW_SCROLL_STEP_PX, behavior: "smooth" });
      }
    }
  };

  const scrollRight = () => {
    if (!isMobile) {
      const node = scrollRef.current?.getScrollableNode();
      if (node) {
        node.scrollBy({ left: ARROW_SCROLL_STEP_PX, behavior: "smooth" });
      }
    }
  };

  useEffect(() => {
    if (isMobile) return;
    const raf = requestAnimationFrame(() => updateScrollEdges());
    return () => cancelAnimationFrame(raf);
  }, [isMobile, children]);

  useEffect(() => {
    return () => stopAutoScroll();
  }, []);

  if (isMobile) {
    return (
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={contentContainerStyle}
        style={style}
      >
        {children}
      </ScrollView>
    );
  }

  return (
    <View
      style={[{ position: "relative" }, style]}
      onMouseEnter={() => !isMobile && setIsRowHovered(true)}
      onMouseLeave={() => setIsRowHovered(false)}
    >
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={contentContainerStyle}
        style={{ flex: 1, width: "100%" }}
        onScroll={() => {
          updateScrollEdges();

          // If the user uses the trackpad or mouse wheel, stop any helper-driven autoscroll.
          const now = performance.now();
          const sinceAuto = now - (lastAutoScrollTsRef.current || 0);
          const sinceWheel = now - (userWheelTsRef.current || 0);
          if (sinceAuto > 120) {
            stopAutoScroll();
          }
          if (sinceWheel < 250) {
            stopAutoScroll();
          }
        }}
        onLayout={updateScrollEdges}
        onWheel={() => {
          userWheelTsRef.current = performance.now();
          stopAutoScroll();
        }}
        scrollEventThrottle={16}
      >
        {children}
      </ScrollView>

      {/* Left Overlay Arrow */}
      {canScrollLeft && (
        <View
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 20,
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            opacity: isRowHovered ? 1 : 0,
            zIndex: 10,
            transition: "opacity 0.2s ease-in-out",
          }}
          pointerEvents={isRowHovered ? "auto" : "none"}
          onMouseEnter={() => !isMobile && startAutoScroll(-1)}
          onMouseLeave={stopAutoScroll}
        >
          <TouchableOpacity
            onPress={scrollLeft}
            style={{
              width: "100%",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Feather
              name="chevron-left"
              size={16}
              color={Colors.darkgrayColor}
            />
          </TouchableOpacity>
        </View>
      )}

      {/* Right Overlay Arrow */}
      {canScrollRight && (
        <View
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: 20,
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            opacity: isRowHovered ? 1 : 0,
            zIndex: 10,
            transition: "opacity 0.2s ease-in-out",
          }}
          pointerEvents={isRowHovered ? "auto" : "none"}
          onMouseEnter={() => !isMobile && startAutoScroll(1)}
          onMouseLeave={stopAutoScroll}
        >
          <TouchableOpacity
            onPress={scrollRight}
            style={{
              width: "100%",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Feather
              name="chevron-right"
              size={16}
              color={Colors.darkgrayColor}
            />
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const DateRangeControl = ({
  hasSelectedBlocks,
  maxWeek,
  startWeek,
  endWeek,
  setStartWeek,
  setEndWeek,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  timePreset,
  setTimePreset,
  t,
}) => {
  const { isMobile } = useDevice();
  const [hoverStart, setHoverStart] = useState(false);
  const [hoverEnd, setHoverEnd] = useState(false);
  const [hoverPreset, setHoverPreset] = useState(false);

  const maxWeekSafe = Math.max(1, Number(maxWeek ?? 1) || 1);

  // Trigger UI only. FloatingMenu handles opening/closing.
  const LabelTrigger = ({ label, value, isHovered, setIsHovered }) => (
    <View
      onMouseEnter={() => !isMobile && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 8,
        backgroundColor: isHovered ? Colors.lightGrayColor : "transparent",
      }}
    >
      <Text style={{ fontSize: 11, color: Colors.grayColor, marginRight: 4 }}>
        {label}
      </Text>
      <Text
        style={{
          fontSize: 13,
          color: Colors.bubbleColor,
          fontWeight: "500",
        }}
      >
        {value}
      </Text>
      <Feather
        name="chevron-down"
        size={14}
        color={Colors.bubbleColor}
        style={{ marginLeft: 2 }}
      />
    </View>
  );

  if (hasSelectedBlocks) {
    // Week Picker
    const startMax = Math.min(maxWeekSafe, Number(endWeek ?? 1) || 1);
    const startWeekItems = Array.from(
      { length: Math.max(0, startMax - 1 + 1) },
      (_, i) => i + 1,
    ).map((w) => ({
      id: String(w),
      value: w,
      label: t("statisticsWeekNumber-string", { week: w }),
    }));

    const endMin = Math.max(1, Number(startWeek ?? 1) || 1);
    const endWeekItems = Array.from(
      { length: Math.max(0, maxWeekSafe - endMin + 1) },
      (_, i) => endMin + i,
    ).map((w) => ({
      id: String(w),
      value: w,
      label: t("statisticsWeekNumber-string", { week: w }),
    }));

    return (
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <View>
          <FloatingMenu
            placement="bottom-start"
            width={160}
            maxHeight={240}
            items={startWeekItems}
            trigger={
              <LabelTrigger
                label={t("statisticsStartLabel-string")}
                value={t("statisticsWeekNumber-string", { week: startWeek })}
                isHovered={hoverStart}
                setIsHovered={setHoverStart}
              />
            }
            renderItem={({ item, close }) => (
              <TouchableOpacity
                onPress={() => {
                  setStartWeek(Math.min(item.value, endWeek));
                  close?.();
                }}
                style={{
                  paddingVertical: 10,
                  paddingHorizontal: 12,
                  width: "100%",
                }}
              >
                <Text style={{ fontSize: 13, color: Colors.blackColor }}>
                  {item.label}
                </Text>
              </TouchableOpacity>
            )}
          />
        </View>

        <View
          style={{
            width: 1,
            height: 12,
            backgroundColor: Colors.borderColor,
            marginHorizontal: 4,
          }}
        />

        <View>
          <FloatingMenu
            placement="bottom-start"
            width={160}
            maxHeight={240}
            items={endWeekItems}
            trigger={
              <LabelTrigger
                label={t("statisticsEndLabel-string")}
                value={t("statisticsWeekNumber-string", { week: endWeek })}
                isHovered={hoverEnd}
                setIsHovered={setHoverEnd}
              />
            }
            renderItem={({ item, close }) => (
              <TouchableOpacity
                onPress={() => {
                  setEndWeek(Math.max(item.value, startWeek));
                  close?.();
                }}
                style={{
                  paddingVertical: 10,
                  paddingHorizontal: 12,
                  width: "100%",
                }}
              >
                <Text style={{ fontSize: 13, color: Colors.blackColor }}>
                  {item.label}
                </Text>
              </TouchableOpacity>
            )}
          />
        </View>
      </View>
    );
  }

  // Time Mode: Presets + Custom
  const presetItems = [
    {
      id: "3m",
      value: "3m",
      labelKey: "statisticsPresetLast3Months-string",
      label: t("statisticsPresetLast3Months-string"),
    },
    {
      id: "6m",
      value: "6m",
      labelKey: "statisticsPresetLast6Months-string",
      label: t("statisticsPresetLast6Months-string"),
    },
    {
      id: "ytd",
      value: "ytd",
      labelKey: "statisticsPresetYearToDate-string",
      label: t("statisticsPresetYearToDate-string"),
    },
    {
      id: "all",
      value: "all",
      labelKey: "statisticsPresetAllTime-string",
      label: t("statisticsPresetAllTime-string"),
    },
    // { id: "custom", value: "custom", label: "Custom" },
  ];

  // Date Picker (Mock or Real)
  const startDateItems = [].map((d) => ({
    id: d,
    value: d,
    label: d,
  }));
  const endDateItems = [].map((d) => ({
    id: d,
    value: d,
    label: d,
  }));

  const currentPresetItem = presetItems.find((p) => p.value === timePreset);
  const currentPresetLabel = currentPresetItem?.label
    ? currentPresetItem.label
    : t("statisticsPresetSelectRange-string");

  return (
    <View style={{ flexDirection: "row", alignItems: "center" }}>
      {/* Preset Selector */}
      <FloatingMenu
        placement="bottom-end"
        width={180}
        items={presetItems}
        trigger={
          <LabelTrigger
            label={t("statisticsRangeLabel-string")}
            value={currentPresetLabel}
            isHovered={hoverPreset}
            setIsHovered={setHoverPreset}
          />
        }
        renderItem={({ item, close }) => (
          <TouchableOpacity
            onPress={() => {
              setTimePreset && setTimePreset(item.value);
              close?.();
            }}
            style={{
              paddingVertical: 10,
              paddingHorizontal: 12,
              width: "100%",
              backgroundColor:
                timePreset === item.value
                  ? Colors.lightGrayColor
                  : "transparent",
            }}
          >
            <Text
              style={{
                fontSize: 13,
                color:
                  timePreset === item.value
                    ? Colors.bubbleColor
                    : Colors.blackColor,
                fontWeight: timePreset === item.value ? "600" : "400",
              }}
            >
              {t(item.labelKey)}
            </Text>
          </TouchableOpacity>
        )}
      />
    </View>
  );
};

const ClientStatisticsHeader = observer(({ store }) => {
  const { isMobile } = useDevice();
  const { screenSize } = useScreenDimensions();
  const isSmall = screenSize === "small";
  const { t, i18n } = useTranslation();
  const [showingPicker, setShowingPicker] = useState(false);
  const addTrackAnchorRef = useRef(null);

  // Time-mode UI preset selection (drives store.startDate/endDate)
  const [timePreset, setTimePreset] = useState("3m");

  const maxWeekAcrossSelectedBlocks = useMemo(() => {
    const selectedBlocks = store?.selectedBlocks ?? [];
    if (!selectedBlocks.length) return 1;

    return selectedBlocks.reduce((maxWeeks, block) => {
      const blockWeeks = Math.max(
        1,
        Number(block?.getWeeks ?? block?.totalWeeks ?? block?.weeks ?? 1) || 1,
      );
      return Math.max(maxWeeks, blockWeeks);
    }, 1);
  }, [store?.selectedBlocks]);

  const startWeek = Number(store?.startWeek ?? 1) || 1;
  const endWeek = Number(store?.endWeek ?? 1) || 1;

  // Keep store week range valid when selected blocks change.
  useEffect(() => {
    if (!store) return;
    if ((store?.selectedBlockIds?.length ?? 0) === 0) return;

    const maxWeek = Math.max(1, Number(maxWeekAcrossSelectedBlocks ?? 1) || 1);
    const nextStart = Math.min(Math.max(1, startWeek), maxWeek);
    const nextEnd = Math.min(Math.max(1, endWeek), maxWeek);

    if (nextStart !== startWeek || nextEnd !== endWeek) {
      store.setWeekRange(
        { startWeek: nextStart, endWeek: nextEnd },
        { t, language: i18n.language },
      );
    }
  }, [
    store,
    maxWeekAcrossSelectedBlocks,
    startWeek,
    endWeek,
    t,
    i18n.language,
  ]);

  // Apply time preset by setting store.startDate/endDate (so the table filters).
  useEffect(() => {
    if (!store) return;
    if ((store?.selectedBlockIds?.length ?? 0) > 0) return; // weeks mode

    const now = new Date();
    let start = new Date(0);
    let end = now;

    if (timePreset === "3m") {
      start = new Date(now);
      start.setMonth(start.getMonth() - 3);
    } else if (timePreset === "6m") {
      start = new Date(now);
      start.setMonth(start.getMonth() - 6);
    } else if (timePreset === "ytd") {
      start = new Date(now.getFullYear(), 0, 1);
    } else if (timePreset === "all") {
      start = new Date(0);
    }

    store.setDateRange(
      { startDate: start, endDate: end },
      { t, language: i18n.language },
    );
  }, [store, timePreset, store?.selectedBlockIds?.length, t, i18n.language]);

  const [allBlocks, blocks] = useMemo(() => {
    const sortedBlocks = store?.client?.sortedBlocks;
    const list = Array.isArray(sortedBlocks)
      ? sortedBlocks
      : (store?.availableBlocks ?? []);
    const all = Array.isArray(list) ? list : [];
    if (!store?.tracks || (store.tracks?.length ?? 0) === 0) return [all, all];
    if (!store?.getEligibleBlockIdsForCurrentTracks) return [all, all];

    const eligible = store.getEligibleBlockIdsForCurrentTracks({
      t,
      language: i18n.language,
    });

    const selectedIds = new Set(store?.selectedBlockIds ?? []);

    // Keep selected blocks visible even if they're not eligible for the
    // currently-selected tracks. Selection should be user-owned and never
    // implicitly removed.
    if (!eligible || eligible.size === 0) {
      return [all, all.filter((b) => selectedIds.has(b?.id))];
    }

    return [
      all,
      all.filter((b) => eligible.has(b?.id) || selectedIds.has(b?.id)),
    ];
  }, [
    store?.client?.sortedBlocks,
    store?.availableBlocks,
    store?.tracks,
    t,
    i18n.language,
  ]);

  const hiddenBlockCount = allBlocks.length - blocks.length;

  const suggestedExerciseNames = useMemo(() => {
    return store?.getSuggestedExerciseDisplayNames
      ? store.getSuggestedExerciseDisplayNames(t, i18n.language)
      : [];
  }, [store, store?.selectedBlockIds?.join("|"), t, i18n.language]);

  const exercisesUnavailableInSelectedBlocks = useMemo(() => {
    const selectedBlocks = store?.selectedBlocks ?? [];
    if (!Array.isArray(selectedBlocks) || selectedBlocks.length === 0) {
      // No block filter → allow all exercises
      return new Set();
    }

    const blockNameSets = selectedBlocks.map((block) => {
      try {
        const names = block?.getAllExercisesNames
          ? block.getAllExercisesNames(t, i18n.language)
          : [];
        return new Set((names ?? []).filter(Boolean));
      } catch (e) {
        // Treat errors as empty sets so we stay safe by disabling unknown items
        return new Set();
      }
    });

    if (blockNameSets.length === 0) return new Set();

    // Exercises allowed are those present in every selected block
    const allowedIntersection = new Set(blockNameSets[0]);
    blockNameSets.slice(1).forEach((nameSet) => {
      Array.from(allowedIntersection).forEach((value) => {
        if (!nameSet.has(value)) {
          allowedIntersection.delete(value);
        }
      });
    });

    const allNames = store?.getAllExerciseDisplayNames
      ? store.getAllExerciseDisplayNames(t, i18n.language)
      : [];

    const unavailable = new Set();
    allNames.forEach((name) => {
      if (name && !allowedIntersection.has(name)) {
        unavailable.add(name);
      }
    });

    return unavailable;
  }, [
    store,
    store?.selectedBlocks,
    store?.selectedBlockIds?.join("|"),
    t,
    i18n.language,
  ]);

  const namesToShow = useMemo(() => {
    const names = new Set();
    (suggestedExerciseNames ?? []).forEach((n) => {
      if (n) names.add(n);
    });
    const pinned = store?.getPinnedExercisesVisibleInScopePublic
      ? store.getPinnedExercisesVisibleInScopePublic({
          t,
          language: i18n.language,
        })
      : [];
    (pinned ?? []).forEach((n) => {
      if (n) names.add(n);
    });
    (store?.tracks ?? []).forEach((tr) => {
      const n = tr?.exerciseDisplayName;
      if (n) names.add(n);
    });
    return Array.from(names).sort((a, b) => String(a).localeCompare(String(b)));
  }, [
    suggestedExerciseNames,
    store?.tracks,
    store?.userPinnedExerciseDisplayNames,
    store?.selectedBlockIds?.join("|"),
    i18n.language,
  ]);

  const helperText = useMemo(() => {
    const numBlocks = store.selectedBlockIds.length;
    const numTracks = store.tracks?.length ?? 0;

    const firstExerciseName =
      numTracks > 0
        ? store.tracks[0]?.exerciseDisplayName ||
          t("statisticsFallbackExerciseName-string")
        : t("statisticsFallbackExerciseName-string");

    const selectedBlockName =
      numBlocks > 0
        ? allBlocks.find((b) => b.id === store.selectedBlockIds[0])?.name ||
          t("statisticsFallbackBlockName-string")
        : t("statisticsFallbackBlockName-string");

    // Case: No block, no exercise
    if (numBlocks === 0 && numTracks === 0) {
      return t("statisticsHelperSelectBlockOrExercises-string");
    }

    // Case: No block, some exercises -> Time Comparison
    if (numBlocks === 0 && numTracks > 0) {
      if (numTracks === 1)
        return t("statisticsHelperAnalyzingSingleAcrossTime-string", {
          exercise: firstExerciseName,
        });
      return t("statisticsHelperComparingExercisesAcrossTime-string", {
        count: numTracks,
      });
    }

    // Case: 1 Block
    if (numBlocks === 1) {
      const bName = selectedBlockName;
      if (numTracks === 0) {
        return t("statisticsHelperSelectOneExercise-string");
      }
      if (numTracks === 1) {
        return t("statisticsHelperAnalyzingInBlock-string", {
          exercise: firstExerciseName,
          block: bName,
        });
      }
      return t("statisticsHelperComparingExercisesInBlock-string", {
        count: numTracks,
        block: bName,
      });
    }

    // Case: >1 Blocks
    if (numBlocks > 1) {
      if (numTracks === 0) {
        return t("statisticsHelperSelectExerciseToCompare-string");
      }
      if (numTracks === 1) {
        return t("statisticsHelperComparingExerciseAcrossBlocks-string", {
          exercise: firstExerciseName,
          blockCount: numBlocks,
        });
      }
      return t("statisticsHelperComparingExercisesAcrossBlocks-string", {
        blockCount: numBlocks,
      });
    }

    return t("statisticsHelperSelectData-string");
  }, [store.selectedBlockIds, allBlocks, store.tracks, t, i18n.language]);

  const addExerciseTooltipContent = store?.canAddTrack
    ? "statisticsAddExerciseTooltip-string"
    : "statisticsUnselectToAdd-string";

  return (
    <View
      style={{
        paddingHorizontal: 16,
        paddingVertical: 16,
        borderRadius: 14,
        backgroundColor: Colors.whiteColor,
        borderWidth: 0.1,
        borderColor: Colors.borderColor,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
          height: 20, // fixed height to prevent layout jumps
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <Text
            style={{
              fontSize: 11,
              color: Colors.grayColor,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontWeight: "600",
            }}
          >
            {t("statisticsBlocksTitle-string")}
          </Text>
        </View>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
          {/* Hidden Text */}
          {hiddenBlockCount > 0 && (
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
            >
              <Ionicons name="filter" size={12} color={Colors.grayColor} />
              <Text
                style={{
                  fontSize: 10,
                  color: Colors.grayColor,
                  fontWeight: "600",
                }}
              >
                {t("statisticsHiddenBlocks-string", {
                  count: hiddenBlockCount,
                })}
              </Text>
            </View>
          )}

          {/* Separator if both present */}
          {store.selectedBlockIds.length >= (store?.maxBlocksAllowed ?? 3) &&
            hiddenBlockCount > 0 && (
              <Text
                style={{
                  fontSize: 10,
                  color: Colors.grayColor,
                  fontWeight: "600",
                }}
              >
                ·
              </Text>
            )}

          {/* Max Reached Text */}
          {store.selectedBlockIds.length >= (store?.maxBlocksAllowed ?? 3) && (
            <Text
              style={{
                fontSize: 10,
                color: Colors.grayColor,
                fontWeight: "600",
              }}
            >
              {t("statisticsMaxSelectionReached-string", {
                count: store?.maxBlocksAllowed ?? 3,
              })}
            </Text>
          )}
        </View>
      </View>

      <ScrollableRow
        contentContainerStyle={{
          alignItems: "center",
          paddingRight: 0,
          gap: PILL_GAP,
        }}
        style={{ maxHeight: 40, marginBottom: 16 }}
      >
        {blocks.length === 0
          ? [0, 1].map((i) => <SkeletonChip key={`skeleton-${i}`} />)
          : blocks.map((b) => (
              <Chip
                key={b.id}
                label={b.name}
                selected={store.selectedBlockIds.includes(b.id)}
                disabled={false}
                onPress={() =>
                  store.toggleBlock(b.id, { t, language: i18n.language })
                }
              />
            ))}
      </ScrollableRow>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
          height: 20,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <Text
            style={{
              fontSize: 11,
              color: Colors.grayColor,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontWeight: "600",
            }}
          >
            {t("statisticsExercisesTitle-string")}
          </Text>
        </View>
        {(store?.tracks?.length ?? 0) >= (store?.maxTracksAllowed ?? 3) && (
          <Text
            style={{
              fontSize: 10,
              color: Colors.grayColor,
              fontWeight: "600",
            }}
          >
            {t("statisticsMaxSelectionReached-string", {
              count: store?.maxTracksAllowed ?? 3,
            })}
          </Text>
        )}
      </View>

      <View style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
        {/* Sticky Add Button */}
        <View ref={addTrackAnchorRef} style={{ marginRight: PILL_GAP }}>
          <Tooltip
            content={addExerciseTooltipContent}
            delayedOpen={store.canAddTrack}
            placement="bottom"
          >
            <View>
              <HoverableAddButton
                onPress={() => setShowingPicker(true)}
                disabled={!store.canAddTrack}
              />
            </View>
          </Tooltip>
        </View>

        <View style={{ flex: 1 }}>
          <ScrollableRow
            contentContainerStyle={{
              alignItems: "center",
              paddingRight: 0,
              gap: PILL_GAP,
            }}
            style={{ maxHeight: 46 }}
          >
            {namesToShow.map((name) => {
              const track = store?.getTrackByDisplayName
                ? store.getTrackByDisplayName(name)
                : null;
              const isSelected = !!track;
              const availableProtocolSlots =
                store?.getAvailableProtocolSlotsForExerciseDisplayName
                  ? store.getAvailableProtocolSlotsForExerciseDisplayName(
                      name,
                      {
                        t,
                        language: i18n.language,
                      },
                    )
                  : [];

              return (
                <ExerciseTrackPill
                  key={name}
                  exerciseDisplayName={name}
                  selected={isSelected}
                  slotMode={track?.slotMode ?? "any"}
                  protocolSlot={track?.protocolSlot ?? null}
                  availableProtocolSlots={availableProtocolSlots}
                  t={t}
                  onToggle={() =>
                    store.toggleTrackByDisplayName(name, {
                      t,
                      language: i18n.language,
                      source: "user-toggle",
                    })
                  }
                  onChangeSlot={(slot) => {
                    const id = store.ensureTrackByDisplayName(name, {
                      t,
                      language: i18n.language,
                    });
                    if (!id) return;
                    store.setTrackSlot(id, slot, {
                      t,
                      language: i18n.language,
                    });
                  }}
                />
              );
            })}
          </ScrollableRow>
        </View>
      </View>

      <StatisticsExerciseSearchFloating
        visible={showingPicker}
        onClose={() => setShowingPicker(false)}
        referenceElement={addTrackAnchorRef.current}
        suggestedExerciseNames={store.getSuggestedExerciseDisplayNames(
          t,
          i18n.language,
        )}
        allExerciseNames={store.getAllExerciseDisplayNames(t, i18n.language)}
        unavailableExerciseNames={exercisesUnavailableInSelectedBlocks}
        onPick={(exerciseDisplayName) => {
          store.toggleTrackByDisplayName(exerciseDisplayName, {
            t,
            language: i18n.language,
            source: "user-search",
          });
        }}
      />

      {/* Bottom Row: Data Scope + Date Controls */}
      <View
        style={{
          marginTop: 16,
          paddingTop: 12,
          borderTopWidth: 1,
          borderTopColor: Colors.borderColor,
          flexDirection: isSmall ? "column" : "row",
          alignItems: isSmall ? "stretch" : "center",
          justifyContent: isSmall ? "space-around" : "space-between",
          gap: isSmall ? 8 : 0,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginRight: isSmall ? 0 : 16,
            ...(!isSmall ? { flex: 1 } : {}),
          }}
        >
          <View
            style={{
              width: 4,
              height: 14,
              backgroundColor: Colors.bubbleColor,
              borderRadius: 2,
              marginRight: 6,
            }}
          />
          <Text
            style={{
              fontSize: 13,
              fontWeight: "600",
              color: Colors.blackColor,
            }}
            numberOfLines={isSmall ? undefined : 1}
          >
            {helperText}
          </Text>
        </View>

        <View
          style={{
            alignSelf: isSmall ? "flex-start" : "auto",
            marginLeft: isSmall ? -8 : 0, // Align Range trigger with helper label in small layout
          }}
        >
          <DateRangeControl
            hasSelectedBlocks={store.selectedBlockIds.length > 0}
            startWeek={startWeek}
            endWeek={endWeek}
            setStartWeek={(w) =>
              store.setWeekRange(
                { startWeek: w, endWeek },
                { t, language: i18n.language },
              )
            }
            setEndWeek={(w) =>
              store.setWeekRange(
                { startWeek, endWeek: w },
                { t, language: i18n.language },
              )
            }
            startDate={store?.startDate}
            endDate={store?.endDate}
            setStartDate={(d) =>
              store.setDateRange(
                { startDate: d, endDate: store?.endDate },
                { t, language: i18n.language },
              )
            }
            setEndDate={(d) =>
              store.setDateRange(
                { startDate: store?.startDate, endDate: d },
                { t, language: i18n.language },
              )
            }
            maxWeek={maxWeekAcrossSelectedBlocks}
            timePreset={timePreset}
            setTimePreset={setTimePreset}
            t={t}
          />
        </View>
      </View>
    </View>
  );
});

export default ClientStatisticsHeader;
